"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.BpmnParserService = exports.ProcessDefinitionRaw = void 0;
const clone = require("lodash.clonedeep");
const index_1 = require("../../Errors/index");
const index_2 = require("../Model/index");
const Logger_1 = require("../../Logger");
const BpmnModelParser_1 = require("./BpmnModelParser");
const logger = new Logger_1.Logger('engine:process_model_parser');
/**
 * Describes a ProcessDefinition, as it is stored in the repository.
 */
class ProcessDefinitionRaw {
}
exports.ProcessDefinitionRaw = ProcessDefinitionRaw;
/**
 * A service that parses, validates and filters ProcessDefinitions and ProcessModels.
 */
class BpmnParserService {
    constructor(iamService) {
        this.bpmnModelParser = new BpmnModelParser_1.BpmnModelParser();
        this.definitionsCache = {};
        this.iamService = iamService;
    }
    /**
     * Takes an XML string and parses it into an internal ProcessDefinition.
     * The definition is then sanity checked, before being returned.
     *
     * @async
     * @param   xml                       The ProcessDefinition's raw xml.
     * @returns                           The parsed ProcessDefinition.
     * @throws {UnprocessableEntityError} If the XML is invalid.
     */
    async parseAndValidateDefinition(xml) {
        let parsedProcessDefinition;
        try {
            parsedProcessDefinition = await this.bpmnModelParser.parseXmlToObjectModel(xml);
            const processModel = parsedProcessDefinition.processes[0];
            const processModelHasNoStartEvents = !processModel.flowNodes.some((flowNode) => flowNode.bpmnType === index_2.BpmnType.startEvent);
            if (processModelHasNoStartEvents) {
                throw new index_1.UnprocessableEntityError(`The ProcessModel \`${processModel.id}\` contained within the diagram \`${parsedProcessDefinition.id}\` has no StartEvents!`);
            }
            const processModelHasNoEndEvents = !processModel.flowNodes.some((flowNode) => flowNode.bpmnType === index_2.BpmnType.endEvent);
            if (processModelHasNoEndEvents) {
                throw new index_1.UnprocessableEntityError(`The ProcessModel \`${processModel.id}\` contained within the diagram \`${parsedProcessDefinition.id}\` has no EndEvents!`);
            }
            return parsedProcessDefinition;
        }
        catch (error) {
            logger.error('Error while parsing and validating definition', { err: error, xml: xml });
            throw error;
        }
    }
    /**
     * Parses the given list of raw ProcessDefinitions into a list of usable ProcessDefinition objects.
     *
     * @async
     * @param   definitionsRaw The list of raw ProcessDefintions.
     * @returns                The list of parsed ProcessDefinitions.
     */
    async parseProcessDefinitions(definitionsRaw) {
        const definitionsList = await Promise.all(definitionsRaw.map(this.parseProcessDefinition.bind(this)));
        return definitionsList;
    }
    /**
     * Parses the given ProcessDefinition's xml into a usable ProcessDefinition object.
     *
     * @async
     * @param  definitionRaw The raw ProcessDefinition to parse.
     * @returns              The parsed ProcessDefinition object.
     */
    async parseProcessDefinition(definitionRaw) {
        const definitionFromCache = clone(this.definitionsCache[definitionRaw.hash]);
        if (definitionFromCache) {
            return definitionFromCache;
        }
        const parsedDefinition = await this.bpmnModelParser.parseXmlToObjectModel(definitionRaw.xml);
        parsedDefinition.hash = definitionRaw.hash;
        parsedDefinition.raw = definitionRaw.xml;
        parsedDefinition.deployedAt = definitionRaw.createdAt;
        parsedDefinition.deployedByUserId = definitionRaw.createdBy;
        this.definitionsCache[definitionRaw.hash] = clone(parsedDefinition);
        return parsedDefinition;
    }
    /**
     * Gets the ProcessModel with the given ID from the given list of raw ProcessDefinitions.
     *
     * @async
     * @param   definitionsRaw The list of raw ProcessDefinitions.
     * @param   processModelId The ID of the ProcessModel to get.
     * @returns                The retrieved ProcessModel.
     * @throws {NotFoundError} If the ProcessModel was not found.
     */
    async retrieveProcessModel(definitionsRaw, processModelId) {
        const processModelList = await this.getProcessModelList(definitionsRaw);
        const matchingProcessModel = processModelList.find((processModel) => {
            return processModel.id === processModelId;
        });
        if (!matchingProcessModel) {
            throw new index_1.NotFoundError(`ProcessModel with id \`${processModelId}\` not found!`);
        }
        return matchingProcessModel;
    }
    /**
     * Parses the list of given raw ProcessDefinitions and gets a list of all ProcessModels contained in those definitions.
     *
     * @async
     * @param   definitionsRaw The list of raw ProcessDefinitions.
     * @returns                The list of parsed ProcessModels retrieved from the definitions.
     */
    async getProcessModelList(definitionsRaw) {
        const definitions = await this.parseProcessDefinitions(definitionsRaw);
        const allProcessModels = [];
        for (const definition of definitions) {
            Array.prototype.push.apply(allProcessModels, definition.processes);
        }
        return allProcessModels;
    }
    /**
     * Takes a ProcessModel and an Identity and filters out all elements of the ProcessModel that the Identity is not allowed to access.
     *
     * @async
     * @param   identity     The identity for which to filter out inaccessible elements.
     * @param   processModel The ProcessModel to filter.
     * @returns              The filtered ProcessModel, or "undefined", if the user is not allowed to access the ProcessModel at all.
     */
    async filterInaccessibleProcessModelElements(identity, processModel) {
        var _a, _b;
        const processModelCopy = clone(processModel);
        const processModelHasNoLanes = !(((_b = (_a = processModel === null || processModel === void 0 ? void 0 : processModel.laneSet) === null || _a === void 0 ? void 0 : _a.lanes) === null || _b === void 0 ? void 0 : _b.length) > 0);
        if (processModelHasNoLanes) {
            return processModelCopy;
        }
        processModelCopy.laneSet = await this.filterOutInaccessibleLanes(processModelCopy.laneSet, identity);
        processModelCopy.flowNodes = this.getFlowNodesForLaneSet(processModelCopy.laneSet, processModel.flowNodes);
        const userCannotAccessAnyStartEvents = !this.checkIfProcessModelHasAccessibleStartEvents(processModelCopy);
        const originalProcessModelHasStartEvents = processModel.flowNodes.some((flowNode) => flowNode.bpmnType === index_2.BpmnType.startEvent);
        if (userCannotAccessAnyStartEvents && originalProcessModelHasStartEvents) {
            return undefined;
        }
        // If the ProcessModel does not have any StartEvents to begin with, it should be returend.
        // Otherwise, the end user would have no way of knowing that he deployed a ProcessModel without StartEvents.
        return processModelCopy;
    }
    /**
     * Takes a laneSet and filters out all lanes that the given identity is not allowed to access.
     *
     * @param   laneSet  The laneSet to filter.
     * @param   identity The identity for which to filter the given laneSet.
     * @returns          The filtered laneSet.
     */
    async filterOutInaccessibleLanes(laneSet, identity) {
        const userHasAdminPrivileges = await this.checkifUserHasAdminPrivileges(identity);
        if (userHasAdminPrivileges) {
            return laneSet;
        }
        const filteredLaneSet = clone(laneSet);
        filteredLaneSet.lanes = [];
        const checkIfUserHasLaneClaim = async (laneName) => {
            try {
                if (!laneName) {
                    return true;
                }
                await this.iamService.ensureHasClaim(identity, laneName);
                return true;
            }
            catch (error) {
                return false;
            }
        };
        for (const lane of laneSet.lanes) {
            const filteredLane = clone(lane);
            const userCanNotAccessLane = !(await checkIfUserHasLaneClaim(lane.name));
            if (userCanNotAccessLane) {
                filteredLane.flowNodeReferences = [];
                delete filteredLane.childLaneSet;
            }
            const laneHasChildLanes = filteredLane.childLaneSet !== undefined;
            if (laneHasChildLanes) {
                filteredLane.childLaneSet = await this.filterOutInaccessibleLanes(filteredLane.childLaneSet, identity);
            }
            filteredLaneSet.lanes.push(filteredLane);
        }
        return filteredLaneSet;
    }
    /**
     * Compares a laneSet against a list of FlowNodes and returns all FlowNodes that are on any of the given lanes.
     * Useful for determining which FlowNodes a user is allowed to access on a filtered laneSet.
     *
     * @param   laneSet   The laneSet to use as a baseline.
     * @param   flowNodes The flowNodes to filter.
     * @returns           The filtered FlowNodes.
     */
    getFlowNodesForLaneSet(laneSet, flowNodes) {
        var _a, _b;
        const accessibleFlowNodes = [];
        for (const lane of laneSet.lanes) {
            // NOTE: flowNodeReferences are stored in both, the parent lane AND in the child lane!
            // So if we have a lane A with two Sublanes B and C, we must not evaluate the elements from lane A!
            // Consider a user who can only access sublane B.
            // If we were to allow him access to all references stored in lane A, he would also be granted access to the elements
            // from lane C, since they are contained within the reference set of lane A!
            const childLaneSetIsEmpty = !(((_b = (_a = lane === null || lane === void 0 ? void 0 : lane.childLaneSet) === null || _a === void 0 ? void 0 : _a.lanes) === null || _b === void 0 ? void 0 : _b.length) > 0);
            if (!childLaneSetIsEmpty) {
                const accessibleChildLaneFlowNodes = this.getFlowNodesForLaneSet(lane.childLaneSet, flowNodes);
                accessibleFlowNodes.push(...accessibleChildLaneFlowNodes);
            }
            else {
                for (const flowNodeId of lane.flowNodeReferences) {
                    const matchingFlowNode = flowNodes.find((flowNode) => {
                        return flowNode.id === flowNodeId;
                    });
                    if (matchingFlowNode) {
                        accessibleFlowNodes.push(matchingFlowNode);
                    }
                }
            }
        }
        return accessibleFlowNodes;
    }
    /**
     * Checks if the given ProcessModel has any StartEvents.
     *
     * @param   processModel The ProcessModel to check.
     * @returns              True, if the ProcessModel has StartEvents; otherwise false.
     */
    checkIfProcessModelHasAccessibleStartEvents(processModel) {
        // For this check to pass, it is sufficient for the ProcessModel to have at least one accessible start event.
        const processModelHasAccessibleStartEvent = processModel.flowNodes.some((flowNode) => {
            return flowNode.bpmnType === index_2.BpmnType.startEvent;
        });
        return processModelHasAccessibleStartEvent;
    }
    async checkifUserHasAdminPrivileges(identity) {
        try {
            const userIsRoot = this.iamService.checkIfTokenIsRootAccessToken(identity.token);
            if (userIsRoot) {
                return true;
            }
            return this.iamService.checkIfUserIsSuperAdmin(identity);
        }
        catch (error) {
            return false;
        }
    }
}
exports.BpmnParserService = BpmnParserService;
//# sourceMappingURL=BpmnParserService.js.map